import { AnimatedHeight } from '@codezo/offer-extras';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { OfferProduct, OfferTier } from '../types';
import {
	AcceptedMark,
	CheckMark,
	EmptyRow,
	MicroLabel,
	RadioMark,
	ScrollX,
	cn,
} from '../ui/primitives';

/**
 * The columns of a tier: mark, quantity, then the figures.
 *
 * Every row declares the same template and spans the product's full width, so a
 * row can be one clickable element instead of eight cells sharing a handler. The
 * first two tracks are fixed rather than `auto` for the same reason — `auto`
 * measures per row, and rows that measure themselves don't line up.
 *
 * Two of those figures are what the offer COSTS us, and a client is never shown
 * either. Dropping them is not a matter of blanking the cells — an empty
 * "Cost/unit" column still tells a client there is a cost we are not naming — so
 * the head, the track and the scroll floor all come off the one list below and
 * can't disagree about which columns exist.
 *
 * ── Why every figure track has a floor ──
 *
 * These used to be `minmax(0, 1fr)`, and a zero floor is a promise a grid cell
 * cannot keep: the track shrinks under its own content and the cell does not
 * truncate, so on any card narrower than the table wanted, a figure ran out
 * through the cell beside it and two numbers read as one. The floors turn that
 * into horizontal scroll, which the `ScrollX` below was already there to absorb.
 *
 * The numbers are measured from the widest value each column can carry, in the
 * type it carries it in — Geist Mono advances ~7.2px a character at 12px and
 * ~7.8px at 13px.
 *
 * ── What is NOT a column ──
 *
 * Margin and Production both were, and both came off: eight columns could not
 * be scaled down to a readable table, and these two were the ones that did not
 * have to be here.
 *
 * Margin, because the rail beside the batch already totals it for the
 * combination on screen — a per-tier margin is a figure you compare, not one you
 * read, and comparing it is what the pricing modal is for.
 *
 * Production, because its content moved rather than vanished: the day range now
 * reads in the hover of the readiness badge on the product header, where it sits
 * next to the dates it produces. The cost of that is real and worth stating — the
 * badge follows the SELECTED tier, so production days can no longer be compared
 * across tiers at a glance, only by selecting each one.
 */
const TIER_FIGURES: Array<{
	head: string;
	/** What the offer costs us or earns — dropped on a client-facing surface. */
	economicsOnly?: boolean;
	/** Narrowest the column may get before the table scrolls instead, in px. */
	floor: number;
	/** Its share of whatever width is left over once every floor is paid. */
	grow: number;
}> = [
	{ head: 'Cost/unit', economicsOnly: true, floor: 78, grow: 1 },
	{ head: 'Price/unit', floor: 78, grow: 1 },
	{ head: 'Cost total', economicsOnly: true, floor: 90, grow: 1 },
	{ head: 'Total', floor: 96, grow: 1 },
];

/** The fixed head of every row, and the gutter it sits in. */
const TIER_MARK = 20;
const TIER_QTY = 55;
/**
 * The row's own controls — the caller's per-tier edit, or the add on an unpriced
 * quantity.
 *
 * Its own track now that Production is gone, rather than riding in the last
 * figure cell. A fixed one: it holds a single icon button, and letting it flex
 * would move the Total column's right edge between a batch that can be edited
 * and one that cannot.
 */
const TIER_ACTIONS = 24;
const TIER_GAP = 20; /* gap-x-5 */
const TIER_PAD = 32; /* px-4, both sides */

const tierFigures = (economics: boolean) =>
	TIER_FIGURES.filter((figure) => economics || !figure.economicsOnly);

function tierGrid(economics: boolean) {
	const tracks = tierFigures(economics).map(
		(figure) => `minmax(${figure.floor}px, ${figure.grow}fr)`
	);
	return `${TIER_MARK}px ${TIER_QTY}px ${tracks.join(' ')} ${TIER_ACTIONS}px`;
}

/**
 * The width the tier table stops shrinking at and starts scrolling.
 *
 * Summed from the same floors rather than stated, because a floor nobody added
 * up is a floor the table quietly breaks through: the old fixed 700 was ~120px
 * short of what the eight admin columns need, which is exactly how the figures
 * came to overlap.
 */
function tierMinWidth(economics: boolean) {
	const figures = tierFigures(economics);
	const floors = figures.reduce((sum, figure) => sum + figure.floor, 0);
	return (
		TIER_MARK +
		TIER_QTY +
		TIER_ACTIONS +
		TIER_PAD +
		/* One gap between every pair of tracks: mark, qty, the figures, actions. */
		TIER_GAP * (figures.length + 2) +
		floors
	);
}

/** Heads for every track. The actions column is unlabelled — it holds controls,
 * not a figure, and a head over it would name a column that states nothing. */
function tierHeads(economics: boolean) {
	return ['', 'Qty', ...tierFigures(economics).map((figure) => figure.head), ''];
}

/**
 * Every product in the offer, with the quantity tiers the client chooses from.
 *
 * One offer, not one per combination: the client assembles their own by picking
 * a tier per product, so what is authored is a grid of possibilities and what is
 * shown is that grid — the previewed pick at full strength, the alternatives
 * dimmed rather than hidden. Hiding them would make a tier look like a decision
 * already taken.
 *
 * Every product is now open in that sense. Combination plans used to pin a
 * quantity, collapsing the covered products to the single row the plan took;
 * they are gone, and with them the idea that anything other than the client's
 * own pick decides which tier applies.
 */
export function OfferProducts({
	products,
	showEconomics = true,
}: {
	products: OfferProduct[];
	/**
	 * Whether the tier table carries what the offer costs us and what it earns.
	 *
	 * Defaults to on — the admin authoring the offer is the caller this table was
	 * drawn for. A client-facing surface passes `false` and gets the same table
	 * with those three columns gone, rather than a second table that would drift
	 * from this one the first time either side changed.
	 */
	showEconomics?: boolean;
}) {
	return (
		<div>
			{products.map((product, i) => (
				<OfferProductBlock
					key={product.id}
					product={product}
					economics={showEconomics}
					last={i === products.length - 1}
				/>
			))}
		</div>
	);
}

function OfferProductBlock({
	product,
	economics,
	last,
}: {
	product: OfferProduct;
	economics: boolean;
	last: boolean;
}) {
	const [open, setOpen] = useState(false);
	const excluded = !product.included;

	return (
		<div className={cn(!last && 'border-b border-line-1')}>
			<div className="flex flex-wrap items-center gap-3 px-4 py-3">
				<button
					type="button"
					disabled={!product.onToggleInclude || !!product.lockedReason}
					onClick={product.onToggleInclude}
					title={
						product.lockedReason ??
						(product.included
							? 'Leave this product out of the previewed order'
							: 'Put this product back in the previewed order')
					}
					aria-label={`Include ${product.product.name} in the previewed order`}
					aria-pressed={product.included}
					className="flex shrink-0 items-center bg-transparent p-0 disabled:cursor-not-allowed"
				>
					<CheckMark on={product.included} muted={!!product.lockedReason} />
				</button>

				{/* The name, and nothing under it. What used to read there — the sku,
				    then how many tiers were priced — is either elsewhere on the page
				    or restated three rows down: the table below this header names
				    every unpriced quantity and says so outright when none is priced. */}
				<div
					className={cn(
						'flex min-w-[160px] flex-1 flex-wrap items-center gap-2 text-base font-medium',
						excluded ? 'text-text-3' : 'text-text-1'
					)}
				>
					{product.product.name}
					{product.nameSlot}
				</div>

				{product.included && product.statusSlot}

				{product.detail && (
					<button
						type="button"
						onClick={() => setOpen((o) => !o)}
						aria-expanded={open}
						className="flex h-7 shrink-0 items-center gap-1 rounded-[var(--r-2)] bg-transparent px-2 text-sm font-medium text-text-2 transition-colors hover:bg-surface-hover hover:text-text-1"
					>
						{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
						Details
					</button>
				)}
			</div>

			{/* Inset in the card's gutter, as the design mounts it. */}
			{product.detail && (
				<AnimatedHeight step={open ? 'open' : 'closed'}>
					{open && <div className="mx-4 mb-3.5">{product.detail}</div>}
				</AnimatedHeight>
			)}

			{excluded && (
				<div className="mx-4 mb-3">
					<EmptyRow>
						{product.excludedNote ??
							'Not included — nothing from this product is priced or shipped.'}
					</EmptyRow>
				</div>
			)}

			{/* The scroll region is this product's tier table, not the module — a
			    caller cannot wrap it, because the header and the spec panel above
			    have to stay put while the figures scroll. */}
			{product.included && (
				<ScrollX>
					<div className="pb-3" style={{ minWidth: tierMinWidth(economics) }}>
						<div
							className="grid gap-x-5 px-4"
							style={{ gridTemplateColumns: tierGrid(economics) }}
						>
							{tierHeads(economics).map((head, i) => (
								<MicroLabel
									key={i}
									className={cn('pt-1 pb-1.5', i > 1 && 'text-right')}
								>
									{head}
								</MicroLabel>
							))}
						</div>

						<div
							role={product.onSelectTier ? 'radiogroup' : undefined}
							aria-label={
								product.onSelectTier
									? `Quantity for ${product.product.name}`
									: undefined
							}
						>
							{product.tiers.map((tier) => (
								<TierRow
									key={tier.id}
									tier={tier}
									economics={economics}
									onSelect={
										product.onSelectTier
											? () => product.onSelectTier?.(tier.id)
											: undefined
									}
									actions={product.renderTierActions?.(tier)}
								/>
							))}
						</div>

						{(product.unpricedQuantities ?? []).map((quantity) => (
							<div
								key={quantity.id}
								role={product.onPriceQuantity ? 'button' : undefined}
								tabIndex={product.onPriceQuantity ? 0 : undefined}
								onClick={product.onPriceQuantity}
								onKeyDown={
									product.onPriceQuantity
										? (e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													product.onPriceQuantity?.();
												}
											}
										: undefined
								}
								title={
									product.onPriceQuantity
										? 'Set a price for this quantity'
										: undefined
								}
								className={cn(
									'grid items-center gap-x-5 border-t border-dashed border-line-2 px-4 py-2.5',
									product.onPriceQuantity &&
										'cursor-pointer hover:bg-surface-hover'
								)}
								style={{ gridTemplateColumns: tierGrid(economics) }}
							>
								<span />
								<span className="font-mono text-base text-text-4">
									{quantity.quantity}
								</span>
								{/* Spans every figure column between the quantity and the
								    actions track, counted off the same list the tracks come
								    from. */}
								<span
									className="text-sm text-text-4"
									style={{ gridColumn: `span ${tierFigures(economics).length}` }}
								>
									Not priced
								</span>
								<span className="flex justify-end text-text-4">
									{product.onPriceQuantity && <Plus size={12} />}
								</span>
							</div>
						))}

						{product.tiers.length === 0 &&
							(product.unpricedQuantities ?? []).length === 0 && (
								<div className="border-t border-line-1 px-4 py-2.5 text-sm text-text-4">
									No pricing set on this product yet.
								</div>
							)}
					</div>
				</ScrollX>
			)}
		</div>
	);
}

function TierRow({
	tier,
	economics,
	onSelect,
	actions,
}: {
	tier: OfferTier;
	/** Whether the cost cells are drawn. Must match the head row's answer — they
	 * share `tierGrid`, so disagreeing shifts every figure. */
	economics: boolean;
	onSelect?: () => void;
	actions?: ReactNode;
}) {
	const live = tier.selected || !!tier.accepted;

	return (
		<div
			role={onSelect ? 'radio' : undefined}
			tabIndex={onSelect ? 0 : undefined}
			aria-checked={onSelect ? tier.selected : undefined}
			onClick={onSelect}
			onKeyDown={
				onSelect
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onSelect();
							}
						}
					: undefined
			}
			title={onSelect ? `Preview ${tier.quantity} in the total` : undefined}
			className={cn(
				'grid items-center gap-x-5 border-t border-line-1 px-4 py-2.5 transition-colors',
				onSelect && 'cursor-pointer hover:bg-surface-hover'
			)}
			style={{
				gridTemplateColumns: tierGrid(economics),
				background: tier.accepted ? 'var(--accent-soft)' : undefined,
			}}
		>
			<span className="flex items-center">
				{tier.accepted ? (
					<AcceptedMark title="The client took this quantity" />
				) : (
					<RadioMark on={tier.selected} />
				)}
			</span>
			{/* Everything but the mark dims on an unselected tier — the row stays
			    readable as an alternative without competing with the live one. */}
			<Cell live={live} className={cn('text-base text-text-1', live && 'font-medium')}>
				{tier.quantity}
			</Cell>
			{economics && (
				<Cell
					live={live}
					className="flex flex-col items-end text-right text-sm text-text-3"
				>
					{tier.unitCost ?? '—'}
					{tier.unitCostNote}
				</Cell>
			)}
			<Cell live={live} className="text-right text-sm text-text-2">
				{tier.unitPrice ?? '—'}
			</Cell>
			{economics && (
				<Cell live={live} className="text-right text-sm text-text-3">
					{tier.baseTotal ?? '—'}
				</Cell>
			)}
			<Cell
				live={live}
				className={cn('text-right text-base text-text-1', live && 'font-medium')}
			>
				{tier.total ?? '—'}
			</Cell>
			<span
				className={cn(
					'flex items-center justify-end transition-opacity',
					live ? 'opacity-100' : 'opacity-45'
				)}
			>
				{actions}
			</span>
		</div>
	);
}

function Cell({
	live,
	className,
	children,
}: {
	live: boolean;
	className?: string;
	children: ReactNode;
}) {
	return (
		<span
			/* `overflow-hidden` is the backstop under the track floors, not a
			   substitute for them. The floors are sized off the widest value each
			   column realistically carries, but they are sized off GLYPH WIDTHS — so
			   if one is ever short, this decides how it fails. Clipped, a figure
			   looks clipped and the reader knows to widen the card; bleeding, it
			   lands on top of the figure beside it and "DKK 100 · 10%" and
			   "DKK 1,100" read as one number that is simply wrong. */
			className={cn(
				'overflow-hidden font-mono transition-opacity',
				live ? 'opacity-100' : 'opacity-45',
				className
			)}
		>
			{children}
		</span>
	);
}
